import { usePermissionsContext } from '..'
import { ProjectPermission } from '../types'
import { Unauthorized } from './Unauthorized'

/**
 * A guard component that controls access to protected content
 * based on project permissions
 *
 * @component
 * @description
 * This component checks if the current user has the required project permission.
 * - Shows Unauthorized component if permission is missing
 * - Renders children if permission exists
 *
 * @param {ProjectPermission} props.permissionName - The required permission to check
 * @param {React.ReactNode} [props.children] - The content to render if permission exists
 *
 * @example
 * ```tsx
 * <ProjectGuard permissionName="webedi:worklist:access">
 *   <WorklistComponent />
 * </ProjectGuard>
 * ```
 *
 * @returns {JSX.Element | null} Returns null while loading, Unauthorized component if no permission,
 * or the children if permission exists
 */

type ProjectGuardProps = {
  permissionName: ProjectPermission
  children?: React.ReactNode
}

export const ProjectGuard: React.FC<ProjectGuardProps> = ({
  permissionName,
  children,
}) => {
  const { permissions } = usePermissionsContext()

  const hasPermission = permissions.data?.some(
    projectPermission =>
      projectPermission.name.toLowerCase() === permissionName.toLowerCase(),
  )

  if (!hasPermission) return <Unauthorized />

  return <>{children}</>
}
